import { Alert, Card, Segmented, Space, Spin, Typography } from "antd";
import type { ReceiveRegionTopVo, SalesRegionVo, VisitorRegionVo } from "@zhihaoscm/entity";
import { useEffect, useState } from "react";
import { Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

export interface AudienceRegionParams {
    productId: string;
    areaLevel: number;
    beginTime?: string;
    endTime?: string;
}

interface RegionPanelState<T> {
    data: T[];
    loading: boolean;
    error: boolean;
}

export interface AudienceProfilePanelProps {
    productId: string;
    startDate: string;
    endDate: string;
    loadVisitorRegion: (params: AudienceRegionParams) => Promise<VisitorRegionVo[]>;
    loadSalesRegion: (params: AudienceRegionParams) => Promise<SalesRegionVo[]>;
    loadReceiveRegion?: (params: {
        productId: string;
        sortType: number;
        beginTime?: string;
        endTime?: string;
    }) => Promise<ReceiveRegionTopVo[]>;
}

const initialVisitorState: RegionPanelState<VisitorRegionVo> = { data: [], loading: true, error: false };
const initialSalesState: RegionPanelState<SalesRegionVo> = { data: [], loading: true, error: false };
const initialReceiveState: RegionPanelState<ReceiveRegionTopVo> = { data: [], loading: true, error: false };

/** Vue 接口返回的占比通常是 0～1，小数或百分比两种格式都兼容展示。 */
const formatProportion = (value: number) => {
    const percentage = value <= 1 ? Math.floor(value * 100) : value;
    return `${percentage}%`;
};

// 地域表列保持 Vue 字段名和摘要单位，避免图表数据迁移后发生展示偏差。
const visitorColumns: TableColumnProps<VisitorRegionVo>[] = [
    { label: "地域", prop: "areaName" },
    {
        label: "访客人数",
        prop: "visitorCount",
        render: ({ row }) => `${row.visitorCount} 人`
    },
    {
        label: "占比",
        prop: "proportion",
        render: ({ row }) => formatProportion(row.proportion)
    }
];

const salesColumns: TableColumnProps<SalesRegionVo>[] = [
    { label: "地域", prop: "areaName" },
    {
        label: "总销量",
        prop: "totalSalesVolume",
        render: ({ row }) => `${row.totalSalesVolume} 吨`
    },
    {
        label: "生成订单",
        prop: "orderCount",
        render: ({ row }) => `${row.orderCount} 个订单`
    }
];

// 收货地排名沿用 Vue 的 TOP5 字段和排序入口，确保切换排序时重新请求接口。
const receiveColumns: TableColumnProps<ReceiveRegionTopVo>[] = [
    { label: "排名", prop: "rank", render: ({ rowIndex }) => rowIndex + 1 },
    { label: "收货城市", prop: "cityName", render: ({ row }) => row.cityName || row.provinceName || "--" },
    { label: "订单量", prop: "orderCount", render: ({ row }) => String(row.orderCount ?? "--") },
    { label: "下单吨数(吨)", prop: "orderTon", render: ({ row }) => String(row.orderTon ?? "--") }
];

function VisitorRegionPanel({
    state,
    onAreaLevelChange,
    areaLevel
}: {
    state: RegionPanelState<VisitorRegionVo>;
    onAreaLevelChange: (value: number) => void;
    areaLevel: number;
}) {
    return (
        <Card
            title="访客地域分布"
            extra={
                <Segmented
                    size="small"
                    value={areaLevel}
                    onChange={value => onAreaLevelChange(Number(value))}
                    options={[
                        { label: "省级", value: 1 },
                        { label: "市级", value: 2 }
                    ]}
                />
            }
        >
            {state.loading ? (
                <Space className="flex w-full justify-center py-8">
                    <Spin aria-label="访客地域分布加载中" />
                </Space>
            ) : state.error ? (
                <Alert type="error" message="访客地域分布加载失败" />
            ) : state.data.length === 0 ? (
                <Typography.Text type="secondary">访客地域分布暂无数据</Typography.Text>
            ) : (
                <ZhTable<VisitorRegionVo>
                    size="small"
                    rowKey={row => String(row.areaName || "")}
                    pagination={false}
                    data={state.data}
                    columns={visitorColumns}
                    emptyText="访客地域分布暂无数据"
                />
            )}
        </Card>
    );
}

function SalesRegionPanel({
    state,
    onAreaLevelChange,
    areaLevel
}: {
    state: RegionPanelState<SalesRegionVo>;
    onAreaLevelChange: (value: number) => void;
    areaLevel: number;
}) {
    return (
        <Card
            title="销售情况地域分布"
            extra={
                <Segmented
                    size="small"
                    value={areaLevel}
                    onChange={value => onAreaLevelChange(Number(value))}
                    options={[
                        { label: "省级", value: 1 },
                        { label: "市级", value: 2 }
                    ]}
                />
            }
        >
            {state.loading ? (
                <Space className="flex w-full justify-center py-8">
                    <Spin aria-label="销售情况地域分布加载中" />
                </Space>
            ) : state.error ? (
                <Alert type="error" message="销售情况地域分布加载失败" />
            ) : state.data.length === 0 ? (
                <Typography.Text type="secondary">销售情况地域分布暂无数据</Typography.Text>
            ) : (
                <ZhTable<SalesRegionVo>
                    size="small"
                    rowKey={row => String(row.areaName || "")}
                    pagination={false}
                    data={state.data}
                    columns={salesColumns}
                    emptyText="销售情况地域分布暂无数据"
                />
            )}
        </Card>
    );
}

function ReceiveRegionPanel({
    state,
    sortType,
    onSortTypeChange
}: {
    state: RegionPanelState<ReceiveRegionTopVo>;
    sortType: number;
    onSortTypeChange: (value: number) => void;
}) {
    return (
        <Card
            title="收货地排名TOP5"
            extra={
                <Segmented
                    size="small"
                    value={sortType}
                    onChange={value => onSortTypeChange(Number(value))}
                    options={[
                        { label: "订单优先", value: 1 },
                        { label: "销量优先", value: 2 }
                    ]}
                />
            }
        >
            {state.loading ? (
                <Space className="flex w-full justify-center py-8">
                    <Spin aria-label="收货地排名加载中" />
                </Space>
            ) : state.error ? (
                <Alert type="error" message="收货地排名加载失败" />
            ) : state.data.length === 0 ? (
                <Typography.Text type="secondary">收货地排名暂无数据</Typography.Text>
            ) : (
                <ZhTable<ReceiveRegionTopVo>
                    aria-label="收货地排名TOP5"
                    rowKey={row => `${row.provinceName}-${row.cityName}`}
                    pagination={false}
                    data={state.data}
                    columns={receiveColumns}
                    emptyText="收货地排名暂无数据"
                />
            )}
        </Card>
    );
}

/** 受众画像的两个地域面板，使用表格摘要承载 Vue 图表的核心数据。 */
export function AudienceProfilePanel({
    productId,
    startDate,
    endDate,
    loadVisitorRegion,
    loadSalesRegion,
    loadReceiveRegion = async () => []
}: AudienceProfilePanelProps) {
    const [areaLevel, setAreaLevel] = useState(1);
    const [receiveSortType, setReceiveSortType] = useState(2);
    const [visitorState, setVisitorState] = useState(initialVisitorState);
    const [salesState, setSalesState] = useState(initialSalesState);
    const [receiveState, setReceiveState] = useState(initialReceiveState);

    useEffect(() => {
        let disposed = false;
        const params: AudienceRegionParams = {
            productId,
            areaLevel,
            beginTime: `${startDate} 00:00:00`,
            endTime: `${endDate} 23:59:59`
        };

        // 两个地域接口独立处理，保证一个接口失败时另一个面板仍能展示结果。
        setVisitorState({ data: [], loading: true, error: false });
        void loadVisitorRegion(params)
            .then(data => {
                if (!disposed) setVisitorState({ data: data || [], loading: false, error: false });
            })
            .catch(() => {
                if (!disposed) setVisitorState({ data: [], loading: false, error: true });
            });

        setSalesState({ data: [], loading: true, error: false });
        void loadSalesRegion(params)
            .then(data => {
                if (!disposed) setSalesState({ data: data || [], loading: false, error: false });
            })
            .catch(() => {
                if (!disposed) setSalesState({ data: [], loading: false, error: true });
            });

        setReceiveState({ data: [], loading: true, error: false });
        void loadReceiveRegion({
            productId,
            sortType: receiveSortType,
            beginTime: params.beginTime,
            endTime: params.endTime
        })
            .then(data => {
                if (!disposed) setReceiveState({ data: data || [], loading: false, error: false });
            })
            .catch(() => {
                if (!disposed) setReceiveState({ data: [], loading: false, error: true });
            });

        return () => {
            disposed = true;
        };
    }, [
        areaLevel,
        endDate,
        loadReceiveRegion,
        loadSalesRegion,
        loadVisitorRegion,
        productId,
        receiveSortType,
        startDate
    ]);

    return (
        <Space direction="vertical" size="middle" className="w-full" aria-label="受众画像地域分布">
            <VisitorRegionPanel state={visitorState} areaLevel={areaLevel} onAreaLevelChange={setAreaLevel} />
            <SalesRegionPanel state={salesState} areaLevel={areaLevel} onAreaLevelChange={setAreaLevel} />
            <ReceiveRegionPanel state={receiveState} sortType={receiveSortType} onSortTypeChange={setReceiveSortType} />
        </Space>
    );
}

export default AudienceProfilePanel;
